import React, { useCallback } from "react";
import { isEqual } from "lodash-es";
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Accordion,
} from "reactstrap";
import { Checkbox, Icon } from "components";
import { useTranslate } from "hooks";
import { useProjectHasProducts } from "hooks/projectHooks";
import cx from "classnames";
import type { CheckboxItemProps } from "components/_Modals/ProjectDownloadModal/ReportsCheckboxes/types";
import { areItemsEqual } from "components/_Modals/ProjectDownloadModal/ReportsCheckboxes/helpers";

export const CheckboxAccordionItem = React.memo<CheckboxItemProps>(
  ({
    item,
    onToggle,
    level = 0,
    expandedAccordionIds,
    setExpandedAccordionIds,
    projectId,
  }) => {
    const { id, translationKey, selected, children } = item;
    const hasChildren = Boolean(children && children.length > 0);
    const translate = useTranslate();
    const toggleAccordion = useCallback(
      (toggleId: string): void => {
        setExpandedAccordionIds((prev: string[]) =>
          prev.includes(toggleId)
            ? prev.filter((a) => a !== toggleId)
            : [...prev, toggleId],
        );
      },
      [setExpandedAccordionIds],
    );

    const handleCheckboxChange = useCallback(() => {
      onToggle(id);
      setExpandedAccordionIds((prevOpen: string[]) => {
        if (!prevOpen.includes(`checkbox-on-${id}`)) {
          return [...prevOpen, `checkbox-on-${id}`];
        }
        return prevOpen;
      });
    }, [id, onToggle, setExpandedAccordionIds]);

    const { hasProducts } = useProjectHasProducts(projectId);

    if (!hasChildren) {
      return (
        <div className="px-3 py-2_5">
          <div className="gap-2 font-md text-gray-700 d-flex align-items-center">
            <Checkbox
              id={`checkbox-${id}`}
              checked={selected}
              onChange={handleCheckboxChange}
              data-testid={`checkbox-on-${id}`}
              wrapperClassName={undefined}
              classNames={undefined}
              disabled={false}
            />
            {translate(translationKey)}
          </div>
        </div>
      );
    }

    return (
      <Accordion open={expandedAccordionIds ?? []} toggle={toggleAccordion}>
        <AccordionItem
          className={cx({
            "border-bottom border-bottom-1 border-bottom-solid": !hasProducts,
          })}
        >
          <AccordionHeader
            targetId={`checkbox-on-${id}`}
            tag="div"
            className={cx("p-2_5 mb-0", {
              "border-bottom border-bottom-1 border-bottom-solid":
                hasChildren && hasProducts,
            })}
          >
            <div
              className={cx(
                "gap-2 font-md text-gray-700 d-flex align-items-center",
                { "cursor-not-allowed pe-none opacity-50": !hasProducts },
              )}
            >
              <Checkbox
                id={`checkbox-${id}`}
                checked={selected}
                onChange={handleCheckboxChange}
                data-testid={`checkbox-on-${id}`}
                wrapperClassName={undefined}
                classNames={undefined}
                disabled={false}
              />
              {translate(translationKey)}
            </div>

            {hasChildren && <Icon iconName="chevron-down-light" />}
          </AccordionHeader>

          <AccordionBody accordionId={`checkbox-on-${id}`}>
            <div className="ps-3">
              {hasProducts ? (
                <div className="px-5 d-flex flex-column">
                  {children?.map((child) => (
                    <CheckboxAccordionItem
                      key={child.id}
                      item={child}
                      onToggle={onToggle}
                      level={level + 1}
                      expandedAccordionIds={expandedAccordionIds}
                      setExpandedAccordionIds={setExpandedAccordionIds}
                    />
                  ))}
                </div>
              ) : (
                <div className="px-5 text-gray-400">
                  {translate("NO_DOCUMENTS_AVAILABLE")}
                </div>
              )}
            </div>
          </AccordionBody>
        </AccordionItem>
      </Accordion>
    );
  },
  (prevProps, nextProps) =>
    areItemsEqual(prevProps.item, nextProps.item) &&
    prevProps.onToggle === nextProps.onToggle &&
    prevProps.level === nextProps.level &&
    isEqual(prevProps.expandedAccordionIds, nextProps.expandedAccordionIds),
);
